/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useContext, useEffect, useRef, useState } from "react"
import { Box } from "@mui/material"
import Target from "@medaica/common/components/icons/target"
import VirtualExamContext from "views/exam/virtual-exam/virtual-exam-context"

const targetHeight = 51
const targetWidth = 51

// Where the crosshair starts: near the bottom right corner of the patient's picture
const initialPosition = { x: 0.88, y: 0.9 }

const clampFraction = (value: number): number => Math.max(Math.min(value, 1), 0)

/**
 * The crosshair the healthcare provider drags over the patient's picture to show the patient where to place the
 * stethoscope. Its position is kept, and sent to the patient, as fractions of the picture's width and height, so it
 * marks the same point of the picture on both screens whatever size each of them sees it at.
 */
const TargetOverlay = (): ReactElement => {
  const [position, setPosition] = useState(initialPosition)
  const [isDragging, setIsDragging] = useState(false)
  const boundingElRef = useRef<HTMLDivElement>(null)
  const targetElRef = useRef<HTMLDivElement>(null)
  const offsetRef = useRef({ x: 0, y: 0 })
  const { avStore } = useContext(VirtualExamContext)

  useEffect(() => {
    avStore.localDataTrack?.send(JSON.stringify({ type: "targetPos", detail: [position.x, position.y] }))
  }, [avStore, position])

  const onDown = (event) => {
    setIsDragging(true)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const targetEl = targetElRef.current!
    targetEl.setPointerCapture(event.pointerId as number)
    const boundingRect = targetEl.getBoundingClientRect()
    offsetRef.current = {
      x: event.clientX - boundingRect.x - targetWidth / 2,
      y: event.clientY - boundingRect.y - targetHeight / 2,
    }
  }

  const onMove = (event) => {
    if (!isDragging) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const boundingElClientRect = boundingElRef.current!.getBoundingClientRect()

    // We set the position of the center of the target and ensure that it doesn't go out of bounds
    setPosition({
      x: clampFraction((event.clientX - boundingElClientRect.x - offsetRef.current.x) / boundingElClientRect.width),
      y: clampFraction((event.clientY - boundingElClientRect.y - offsetRef.current.y) / boundingElClientRect.height),
    })
  }

  const onUp = () => setIsDragging(false)

  const left = `${position.x * 100}%`
  const top = `${position.y * 100}%`

  return (
    <Box ref={boundingElRef} sx={{ position: "absolute", height: "100%", width: "100%" }}>
      <Box
        data-testid="target"
        ref={targetElRef}
        sx={{
          height: `${targetHeight}px`,
          width: `${targetWidth}px`,
          position: "absolute",
          left: `calc(${left} - ${targetWidth / 2}px)`,
          top: `calc(${top} - ${targetHeight / 2}px)`,
          touchAction: "none",
          borderRadius: "50%",
          opacity: isDragging ? 1 : 0.8,
          cursor: "move",
          zIndex: 100,
        }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <Target />
      </Box>
      <Box
        sx={{
          position: "absolute",
          backgroundColor: "white",
          height: "1px",
          width: "100%",
          top: `calc(${top} - 0.5px)`,
          left: 0,
          opacity: 0.4,
          zIndex: 10,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          backgroundColor: "white",
          height: "100%",
          width: "1px",
          top: 0,
          left: `calc(${left} - 0.5px)`,
          opacity: 0.4,
          zIndex: 10,
        }}
      />
    </Box>
  )
}

export default TargetOverlay
